import { Howler } from "howler";

export function createAlertSoundGuard({
  howler = Howler,
  target = typeof document !== "undefined" ? document : null,
} = {}) {
  const blockedSounds = new Set();
  const failedSounds = new Set();
  const suspendedSounds = new Set();
  const loadedSounds = new WeakSet();
  const retiredSounds = new WeakSet();
  const reloaders = new WeakMap();
  const listeners = new Set();
  let armed = false;
  let snapshot = { blocked: false, loadFailed: false };

  const isActive = (sound) => !suspendedSounds.has(sound);
  const hasActive = (sounds) => [...sounds].some(isActive);

  const emit = () => {
    snapshot = {
      blocked: hasActive(blockedSounds),
      loadFailed: hasActive(failedSounds),
    };
    listeners.forEach((listener) => listener());
  };

  const onActivation = () => {
    disarm();
    setTimeout(retry, 0);
  };

  function arm() {
    if (armed || !target) return;
    armed = true;
    target.addEventListener("click", onActivation, true);
    target.addEventListener("keydown", onActivation, true);
  }

  function disarm() {
    if (!armed) return;
    armed = false;
    target.removeEventListener("click", onActivation, true);
    target.removeEventListener("keydown", onActivation, true);
  }

  function retry() {
    disarm();
    if (howler?.ctx && howler.ctx.state !== "running") {
      howler.ctx.resume().catch(() => {});
    }
    const pending = [...blockedSounds].filter(isActive);
    pending.forEach((sound) => blockedSounds.delete(sound));
    emit();
    pending.forEach((sound) => sound.play());
  }

  function forget(sound) {
    retiredSounds.add(sound);
    reloaders.delete(sound);
    suspendedSounds.delete(sound);
    const wasBlocked = blockedSounds.delete(sound);
    const wasFailed = failedSounds.delete(sound);
    return wasBlocked || wasFailed;
  }

  const isReported = (sound) =>
    blockedSounds.has(sound) || failedSounds.has(sound);

  function suspend(sound) {
    if (!sound || retiredSounds.has(sound) || suspendedSounds.has(sound)) {
      return;
    }
    suspendedSounds.add(sound);
    if (isReported(sound)) emit();
  }

  function resume(sound) {
    if (!suspendedSounds.delete(sound)) return;
    if (blockedSounds.has(sound)) arm();
    if (isReported(sound)) emit();
  }

  function replace(previous, next) {
    if (!previous || previous === next) return;
    const wasFailed = failedSounds.has(previous);
    const changed = forget(previous);
    if (wasFailed && next && !retiredSounds.has(next)) failedSounds.add(next);
    if (changed) emit();
  }

  function reload() {
    [...failedSounds]
      .filter(isActive)
      .forEach((sound) => reloaders.get(sound)?.());
  }

  const guard = (sound, { reload: reloadSound } = {}) => {
    if (typeof reloadSound === "function") reloaders.set(sound, reloadSound);
    const stop = sound.stop;
    sound.stop = function (...args) {
      if (blockedSounds.delete(sound)) emit();
      return stop.apply(this, args);
    };
    const unload = sound.unload;
    if (typeof unload === "function") {
      sound.unload = function (...args) {
        if (forget(sound)) emit();
        return unload.apply(this, args);
      };
    }
    sound.on("playerror", () => {
      if (retiredSounds.has(sound)) return;
      blockedSounds.add(sound);
      arm();
      emit();
    });
    sound.on("loaderror", () => {
      if (retiredSounds.has(sound) || loadedSounds.has(sound)) return;
      failedSounds.add(sound);
      emit();
    });
    sound.on("load", () => {
      loadedSounds.add(sound);
      if (failedSounds.delete(sound)) emit();
    });
    return sound;
  };

  return {
    guard,
    replace,
    reload,
    retry,
    suspend,
    resume,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
  };
}

const alertSoundGuard = createAlertSoundGuard();

export const guardAlertSound = alertSoundGuard.guard;
export const replaceAlertSound = alertSoundGuard.replace;
export const reloadFailedAlertSounds = alertSoundGuard.reload;
export const retryBlockedAlertSounds = alertSoundGuard.retry;
export const suspendAlertSound = alertSoundGuard.suspend;
export const resumeAlertSound = alertSoundGuard.resume;
export const subscribeAlertSound = alertSoundGuard.subscribe;
export const getAlertSoundSnapshot = alertSoundGuard.getSnapshot;
